import { SupportLayout } from "@/components/support-layout";

export const Terms = () => {
  return (
    <SupportLayout>
      <div>
          <h1 className="text-3xl font-bold mb-2">Terms of Use</h1>
          
          <div className="text-sm text-muted-foreground mb-8">
            <p>Last Updated: 16 December 2025</p>
            <p>Effective Date: 16 December 2025</p>
          </div>

          <div className="prose prose-invert max-w-none space-y-6 text-sm leading-relaxed">
            <p>
              These Terms of Use ("Terms") govern your access to and use of the Nordex software, websites, mobile applications, APIs, and related services (collectively, the "Interface" or the "Platform"), provided by Nordex / AltariGroup ("Nordex", "we", "us", or "our").
            </p>
            
            <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 my-6">
              <p className="font-semibold text-destructive mb-2">ARBITRATION NOTICE</p>
              <p className="text-sm">
                THESE TERMS CONTAIN AN ARBITRATION CLAUSE. BY ACCEPTING THESE TERMS, YOU AGREE THAT DISPUTES BETWEEN YOU AND NORDEX WILL BE RESOLVED BY BINDING, INDIVIDUAL ARBITRATION, AND YOU WAIVE YOUR RIGHT TO A TRIAL BY JURY OR TO PARTICIPATE AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR OTHER REPRESENTATIVE PROCEEDING.
              </p>
            </div>

            <p>
              By accessing or using the Interface, you acknowledge that you have read, understood, and agreed to be bound by these Terms.
              If you do not agree to these Terms, you are not authorized to access or use the Interface.
            </p>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">1. The Interface</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">1.1 Nature of the Interface</h3>
              <p>
                Nordex provides a software-based, non-custodial user interface that enables users to independently interact with blockchain networks, smart contracts, and third-party decentralized or protocol-based systems.
              </p>
              <p className="mt-4">The Interface may allow users to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Connect third-party wallets or authentication providers (including, but not limited to, Privy)</li>
                <li>View market data, analytics, and trading interfaces</li>
                <li>Submit user-authorized and user-signed transaction requests</li>
                <li>Interact with protocol-based perpetual trading mechanisms</li>
                <li>Access web, mobile, and API-based interfaces</li>
              </ul>
              <p className="mt-4">
                Nordex does not operate, own, control, or manage any blockchain network, validator set, liquidity pool, or decentralized protocol.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">1.2 No Exchange, Broker, or Custodian</h3>
              <p>Nordex is not:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>A cryptocurrency exchange</li>
                <li>A broker, dealer, or intermediary</li>
                <li>A clearing house</li>
                <li>A custodian or wallet provider</li>
                <li>A bank or financial institution</li>
                <li>A money transmitter or money services business</li>
              </ul>
              <p className="mt-4">
                Nordex does not take custody of user assets and does not execute trades on behalf of users. All transactions are peer-to-peer.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">2. Wallets, Keys, and User Control</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">2.1 Self-Custody and Authentication</h3>
              <p>
                To use the Interface, you must connect a non-custodial wallet or use a third-party authentication service (such as Privy) that you choose.
              </p>
              <p className="mt-4">Nordex:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Does not store private keys, seed phrases, passwords, or authentication credentials</li>
                <li>Cannot access, recover, or restore your assets or account</li>
                <li>Cannot initiate, authorize, reverse, or modify transactions on your behalf</li>
              </ul>
              
              <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 my-4">
                <p className="font-semibold text-destructive mb-2">CRITICAL SECURITY WARNING</p>
                <p className="text-sm">
                  YOU ACKNOWLEDGE AND AGREE THAT NORDEX DOES NOT STORE AND IS NOT RESPONSIBLE IN ANY WAY FOR THE SECURITY OF YOUR PRIVATE KEYS, SEED PHRASES, PASSWORDS, OR AUTHENTICATION METHODS. IF YOU LOSE ACCESS TO YOUR AUTHENTICATION METHOD, YOU MAY PERMANENTLY LOSE ACCESS TO YOUR ASSETS. NORDEX CANNOT AND WILL NOT RECOVER LOST CREDENTIALS OR ASSETS.
                </p>
              </div>

              <p className="mt-4">
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Securing your wallet, credentials, and authentication methods</li>
                <li>Maintaining confidentiality of your private keys, seed phrases, and passwords</li>
                <li>All activities that occur under your account or wallet address</li>
                <li>Any loss of assets due to failure to secure your authentication methods</li>
              </ul>

              <p className="mt-4 font-semibold">
                Anyone who has access to your private keys, seed phrases, or authentication credentials can access, transfer, or spend your assets.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">2.2 Irreversible Transactions</h3>
              <p>
                Blockchain transactions are irreversible and final.
                Nordex has no ability to reverse, cancel, modify, or refund transactions.
                There are no refunds for any transactions conducted through the Interface.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">3. Third-Party Protocols and Services</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">3.1 Reliance on Third Parties</h3>
              <p>
                The Interface may integrate with or display information from third-party protocols, smart contracts, liquidity sources, authentication providers, or infrastructure providers ("Third-Party Services").
              </p>
              <p className="mt-4">Nordex does not control, audit, or guarantee:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Execution, settlement, or clearing of transactions</li>
                <li>Pricing or price accuracy</li>
                <li>Liquidity or availability</li>
                <li>Security or reliability</li>
                <li>Functionality or performance</li>
              </ul>
              <p className="mt-4">
                Your interaction with Third-Party Services is entirely at your own risk. Nordex has no responsibility or liability for Third-Party Services.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">3.2 Protocol-Defined Logic</h3>
              <p>
                Trading logic — including order matching, pricing, funding rates, margin requirements, and liquidation mechanics — may be determined by protocol rules or smart contracts, not by Nordex.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">4. Eligibility</h2>
              <p>By using the Interface, you represent and warrant that:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You are at least 18 years old (or the age of majority in your jurisdiction, whichever is higher)</li>
                <li>You have legal capacity to enter into these Terms and be bound by them</li>
                <li>If entering into these Terms for an entity, you have legal authority to bind that entity</li>
                <li>Your use of the Interface is lawful in your jurisdiction</li>
                <li>You are not subject to economic or trade sanctions administered by any governmental authority</li>
                <li>You are not designated on any list of prohibited or restricted parties</li>
                <li>You do not intend to transact with any Restricted Person or Sanctioned Person</li>
              </ul>
              <p className="mt-4">
                Nordex may restrict or deny access at its sole discretion, at any time, with or without cause or notice.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">5. Restricted Jurisdictions & Geo-Blocking</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">5.1 Restricted Jurisdictions</h3>
              <p>The Interface is not available to users who are:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Located in, resident of, or citizens of jurisdictions where digital asset trading, leveraged trading, or derivatives are restricted or prohibited</li>
                <li>Subject to economic sanctions or export controls</li>
                <li>Listed on sanctions lists maintained by authorities including (but not limited to):
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>United Nations Security Council</li>
                    <li>United States (OFAC)</li>
                    <li>European Union</li>
                    <li>United Kingdom (HM Treasury)</li>
                  </ul>
                </li>
                <li>Located in, resident of, or citizens of: Iran, Cuba, North Korea, Syria, Myanmar (Burma), the regions of Crimea, Donetsk, or Luhansk, or any other jurisdiction subject to comprehensive sanctions</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">5.2 Geo-Blocking</h3>
              <p>
                Nordex may implement IP-based, geographic, device-level, or account-level restrictions to prevent access from certain jurisdictions.
              </p>
              <p className="mt-4">
                Availability may change at any time without notice. Nordex reserves the right to restrict access from any jurisdiction at its sole discretion.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">5.3 No Circumvention</h3>
              <p>You agree not to bypass restrictions by:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Using VPNs, proxies, or anonymization tools</li>
                <li>Masking IP addresses or device identifiers</li>
                <li>Providing false residency or location information</li>
                <li>Using any other method to disguise your location</li>
              </ul>
              <p className="mt-4 font-semibold text-destructive">
                Circumvention of geographic restrictions may result in immediate suspension or termination without notice.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">5.4 User Responsibility</h3>
              <p>
                You are solely responsible for determining whether your use of the Interface is legal in your jurisdiction.
                Nordex does not provide legal advice regarding jurisdictional compliance.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">6. Trading Risks</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">6.1 General Risk Disclosure</h3>
              <p>
                You acknowledge that trading digital assets and derivatives involves substantial risk, including total loss of funds.
              </p>
              <p className="mt-4">Risks include, but are not limited to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Extreme price volatility and rapid price movements</li>
                <li>Leverage amplification of losses</li>
                <li>Liquidation events without prior notice</li>
                <li>Funding payments between traders</li>
                <li>Oracle delays, failures, or inaccuracies</li>
                <li>Smart contract vulnerabilities or exploits</li>
                <li>Network congestion, failures, or forks</li>
                <li>Third-party service outages or failures</li>
                <li>Regulatory actions or changes</li>
                <li>Cybersecurity threats and attacks</li>
                <li>Loss of private keys or authentication credentials</li>
                <li>Advances in cryptography (including quantum computing)</li>
              </ul>

              <p className="mt-4 font-semibold text-destructive">
                The risk of loss in trading digital assets can be substantial. You should carefully assess whether such trading is suitable for you in light of your circumstances and financial resources.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">6.2 Liquidation Risk</h3>
              <p>
                Positions may be liquidated automatically without prior notice if margin requirements are not met.
                Nordex does not guarantee liquidation pricing, timing, or outcomes. You are solely responsible for maintaining adequate margin.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">6.3 Technology Risks</h3>
              <p>
                You understand that blockchain networks and smart contracts remain under development and are subject to technological and security risks. The Interface relies on emerging technologies that may be subject to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Software bugs or vulnerabilities</li>
                <li>Protocol changes or forks</li>
                <li>Network congestion or downtime</li>
                <li>Transaction delays or failures</li>
                <li>Increased transaction costs</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">6.4 Regulatory Risks</h3>
              <p>
                The Interface and your digital assets could be impacted by regulatory inquiries or actions, which could impede or limit the ability of Nordex to continue making the Interface available or limit your ability to access or use the Interface.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">7. No Advice or Fiduciary Duty</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">7.1 No Professional Advice</h3>
              <p>Nordex does not provide:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Investment advice or recommendations</li>
                <li>Trading advice or strategies</li>
                <li>Financial, legal, or tax advice</li>
                <li>Any form of professional advice</li>
              </ul>
              <p className="mt-4">
                Nothing on the Interface constitutes a recommendation, solicitation, or offer to buy or sell any digital asset.
                All information provided is for informational purposes only.
              </p>
              <p className="mt-4">
                Before making any financial, legal, or other decisions involving the Interface, you should seek independent professional advice from an individual who is licensed and qualified in the appropriate area.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">7.2 No Fiduciary Duties</h3>
              <p>
                These Terms are not intended to, and do not, create or impose any fiduciary duties on Nordex.
              </p>
              <p className="mt-4 font-semibold">
                To the fullest extent permitted by law, you acknowledge and agree that Nordex owes no fiduciary duties or liabilities to you or any other party. To the extent any such duties or liabilities may exist at law or in equity, you hereby irrevocably disclaim, waive, and eliminate those duties and liabilities.
              </p>
              <p className="mt-4">
                You agree that the only duties and obligations that Nordex owes you are those expressly set out in these Terms.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">8. Fees</h2>
              <p>You may incur fees including:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Blockchain network (gas) fees</li>
                <li>Protocol-level fees</li>
                <li>Trading fees (maker/taker)</li>
                <li>Funding rate payments</li>
                <li>Liquidity provider fees</li>
                <li>Third-party service fees</li>
              </ul>
              <p className="mt-4">
                Nordex may receive fees or revenue shares from integrations with third parties.
                All fees may change at any time without notice. Fee information displayed on the Interface is an estimate only and may vary from actual fees paid.
              </p>
              <p className="mt-4">
                You are responsible for ensuring you have adequate funds to complete transactions. Nordex will not be liable for any failed transactions due to insufficient funds.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">9. Acceptable Use and Prohibited Activities</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">9.1 Prohibited Activities</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Manipulate markets, prices, or protocols</li>
                <li>Engage in wash trading, spoofing, or front-running</li>
                <li>Exploit vulnerabilities or bugs</li>
                <li>Abuse APIs or infrastructure</li>
                <li>Circumvent geographic restrictions or security measures</li>
                <li>Engage in unlawful conduct or facilitate illegal activity</li>
                <li>Use the Interface for money laundering, terrorist financing, or sanctions evasion</li>
                <li>Provide false, inaccurate, or misleading information</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the Interface or other users' access</li>
                <li>Use automated tools (bots, scrapers) without authorization</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Interface</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">9.2 Enforcement</h3>
              <p>
                Nordex may suspend or terminate access at any time, with or without cause or notice, if we believe you have violated these Terms or engaged in prohibited activities.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">10. Availability and Modifications</h2>
              <p>
                The Interface may be inaccessible or inoperable for any reason, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Equipment, technology, or infrastructure malfunctions</li>
                <li>Periodic maintenance or repairs</li>
                <li>Causes beyond Nordex's control</li>
                <li>Disruptions to underlying blockchain infrastructure</li>
                <li>Unavailability of third-party services</li>
              </ul>
              <p className="mt-4">
                Nordex may modify, suspend, or discontinue any feature of the Interface at any time without notice.
                We reserve the right to disable or modify access to the Interface at any time for any reason.
              </p>
              <p className="mt-4">
                Nordex will not be liable for any losses or damages resulting from the Interface being inaccessible or inoperable.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">11. Warranty Disclaimers</h2>
              
              <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 my-4">
                <p className="font-semibold text-destructive mb-3">IMPORTANT DISCLAIMER</p>
                <p className="text-sm mb-3">
                  YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT USE OF THE INTERFACE IS AT YOUR SOLE RISK AND THAT THE ENTIRE RISK AS TO SATISFACTORY QUALITY, PERFORMANCE, ACCURACY, AND EFFORT IS WITH YOU.
                </p>
                <p className="text-sm mb-3">
                  THE INTERFACE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY REPRESENTATION OR WARRANTY, WHETHER EXPRESS, IMPLIED, OR STATUTORY. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NORDEX SPECIFICALLY DISCLAIMS ANY EXPRESS OR IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND/OR NON-INFRINGEMENT.
                </p>
                <p className="text-sm">
                  NORDEX DOES NOT MAKE ANY REPRESENTATIONS OR WARRANTIES THAT ACCESS TO THE INTERFACE OR ANY OF THE MATERIALS CONTAINED THEREIN WILL BE CONTINUOUS, UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE.
                </p>
              </div>

              <p className="mt-4">
                Nordex does not warrant or guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Accuracy, completeness, or reliability of information</li>
                <li>Availability or uptime of the Interface</li>
                <li>Security or freedom from viruses or harmful code</li>
                <li>Fitness for a particular purpose</li>
                <li>That errors or defects will be corrected</li>
                <li>That transactions will be executed at desired prices or at all</li>
                <li>The operation or availability of blockchain networks</li>
              </ul>

              <p className="mt-4">
                Some jurisdictions do not allow the exclusion of implied warranties, so some of the above exclusions may not apply to you.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">12. Limitation of Liability</h2>
              
              <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 my-4">
                <p className="font-semibold text-destructive mb-3">LIMITATION OF LIABILITY</p>
                <p className="text-sm mb-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, NORDEX AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND REPRESENTATIVES (COLLECTIVELY, "NORDEX PARTIES") SHALL NOT BE LIABLE FOR ANY INCIDENTAL, INDIRECT, SPECIAL, PUNITIVE, CONSEQUENTIAL, OR SIMILAR DAMAGES OR LIABILITIES WHATSOEVER (INCLUDING DAMAGES FOR LOSS OF ASSETS, DATA, REVENUE, PROFITS, OR OTHER BUSINESS OR FINANCIAL BENEFIT) ARISING OUT OF OR IN CONNECTION WITH THE INTERFACE.
                </p>
                <p className="text-sm">
                  THIS LIMITATION APPLIES WHETHER UNDER CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, BREACH OF WARRANTIES, OR ANY OTHER THEORY OF LIABILITY, AND WHETHER OR NOT NORDEX HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
              </div>

              <p className="mt-4">
                To the maximum extent permitted by law, Nordex Parties shall not be liable for:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Loss of assets or digital assets</li>
                <li>Trading losses or missed profits</li>
                <li>Liquidations or funding payments</li>
                <li>Missed or failed executions</li>
                <li>Oracle inaccuracies or delays</li>
                <li>Smart contract failures or exploits</li>
                <li>Network outages or congestion</li>
                <li>Third-party failures or actions</li>
                <li>Loss of private keys or credentials</li>
                <li>Unauthorized access to your account</li>
                <li>Errors in pricing or data feeds</li>
                <li>Delays in deposits, withdrawals, or transactions</li>
              </ul>

              <p className="mt-4 font-semibold">
                IN NO EVENT SHALL THE TOTAL LIABILITY OF NORDEX PARTIES TO YOU FOR ALL DAMAGES EXCEED THE GREATER OF (A) THE AMOUNT OF FEES PAID BY YOU TO NORDEX IN THE THREE (3) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR (B) ONE HUNDRED U.S. DOLLARS ($100.00).
              </p>

              <p className="mt-4 font-semibold text-destructive">
                Your use of the Interface is at your own risk. You bear the entire risk of loss from your use of the Interface.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">13. Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless Nordex Parties from and against any and all claims, demands, lawsuits, actions, proceedings, investigations, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your access to or use of the Interface</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any applicable laws or regulations</li>
                <li>Your infringement or misappropriation of any third-party rights</li>
                <li>Digital assets associated with your wallet address</li>
                <li>Any content or information you provide</li>
              </ul>
              <p className="mt-4">
                If you are obligated to indemnify Nordex, Nordex will have the right, in its sole discretion, to control any action or proceeding and determine whether to settle, and if so, on what terms.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">14. Termination and Suspension</h2>
              <p>
                Nordex may suspend or terminate your access to the Interface at any time, with or without cause, with or without notice, and without incurring any liability.
              </p>
              <p className="mt-4">
                Reasons for suspension or termination may include:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Violation of these Terms</li>
                <li>Suspicious or fraudulent activity</li>
                <li>Abuse of the Interface</li>
                <li>Regulatory requirements</li>
                <li>Security concerns</li>
                <li>At Nordex's sole discretion</li>
              </ul>
              <p className="mt-4">
                Upon termination, your right to use the Interface will immediately cease. The following sections will survive termination: Sections 2, 7, 11, 12, 13, 15, 16, and 17.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">15. Dispute Resolution and Arbitration</h2>
              
              <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4 my-4">
                <p className="font-semibold text-destructive mb-2">PLEASE READ THIS SECTION CAREFULLY</p>
                <p className="text-sm">
                  THIS SECTION LIMITS YOUR RIGHTS IN THE EVENT OF A DISPUTE. IT REQUIRES YOU TO SUBMIT DISPUTES TO BINDING INDIVIDUAL ARBITRATION AND WAIVE YOUR RIGHT TO A TRIAL BY JURY OR TO PARTICIPATE IN CLASS ACTIONS.
                </p>
              </div>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.1 Informal Resolution</h3>
              <p>
                Before filing any formal dispute, you agree to first contact Nordex via our <a href="/contact" className="text-primary hover:underline">contact page</a> to attempt to resolve the dispute informally. You must include:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Your name and contact information</li>
                <li>A detailed description of the dispute</li>
                <li>The relief sought</li>
              </ul>
              <p className="mt-4">
                If the dispute is not resolved within sixty (60) days after notice, either party may initiate arbitration.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.2 Binding Arbitration</h3>
              <p>
                You and Nordex agree that any dispute arising out of or relating to these Terms or the Interface shall be resolved through final and binding arbitration, rather than in court, except as set forth below.
              </p>
              <p className="mt-4">
                The arbitration will be administered by JAMS (Judicial Arbitration and Mediation Services) under its Comprehensive Arbitration Rules and Procedures, available at https://www.jamsadr.com/rules-comprehensive-arbitration/.
              </p>
              <p className="mt-4">
                The arbitration will be conducted by a single arbitrator. The arbitrator will have exclusive authority to resolve all disputes, including the arbitrability of any dispute.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.3 Arbitration Procedures</h3>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>The arbitration will be conducted in English</li>
                <li>The arbitration may be conducted via videoconference, telephonically, or based on written submissions</li>
                <li>The arbitrator may award any remedies available under applicable law</li>
                <li>The arbitration proceeding and any award shall be confidential</li>
                <li>Judgment on the award may be entered in any court having jurisdiction</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.4 Class Action Waiver</h3>
              <p className="font-semibold">
                YOU AND NORDEX AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.
              </p>
              <p className="mt-4">
                The arbitrator may not consolidate more than one person's claims and may not preside over any form of representative or class proceeding.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.5 Exceptions</h3>
              <p>
                Either party may bring an action in court:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>For disputes that qualify for small claims court</li>
                <li>To seek injunctive or other equitable relief for intellectual property infringement</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.6 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, without regard to conflict of law principles.
              </p>
              <p className="mt-4">
                To the extent arbitration does not apply, you agree to submit to the exclusive jurisdiction of the courts of the United Arab Emirates.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">15.7 Time Limitation</h3>
              <p>
                Any claim arising out of or related to these Terms or the Interface must be filed within one (1) year after the claim arose; otherwise, the claim is permanently barred.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">16. Changes to Terms</h2>
              <p>
                Nordex may update or modify these Terms at any time at our sole discretion. If we make changes, we will notify you by:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Posting the modified Terms on the Interface</li>
                <li>Updating the "Last Updated" date</li>
                <li>Providing notice through the Interface or via email</li>
              </ul>
              <p className="mt-4">
                Your continued use of the Interface after any modifications constitutes acceptance of the updated Terms.
                If you do not agree to the modified Terms, you must stop using the Interface.
              </p>
              <p className="mt-4">
                Unless expressly stated otherwise, modifications to these Terms shall apply to all claims, actions, and disputes arising at any time, including those that accrued prior to the effective date of such modifications.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">17. General Provisions</h2>
              
              <h3 className="text-lg font-semibold mb-3 mt-6">17.1 Entire Agreement</h3>
              <p>
                These Terms constitute the entire agreement between you and Nordex regarding the Interface and supersede all prior agreements and understandings.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.2 Severability</h3>
              <p>
                If any provision of these Terms is held invalid or unenforceable, that provision will be enforced to the maximum extent permissible and the other provisions will remain in full force and effect.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.3 Waiver</h3>
              <p>
                No waiver of any provision of these Terms shall be deemed a further or continuing waiver of such provision or any other provision.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.4 Assignment</h3>
              <p>
                You may not assign or transfer these Terms without Nordex's prior written consent. Nordex may freely assign or transfer these Terms without restriction.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.5 Force Majeure</h3>
              <p>
                Nordex shall not be liable for any delay or failure in performance resulting from events beyond its reasonable control, including natural disasters, internet outages, regulatory actions, or third-party service interruptions.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.6 Electronic Communications</h3>
              <p>
                You consent to receive all communications, agreements, documents, receipts, notices, and disclosures electronically. You agree that we may provide communications to you by posting them on the Interface or by emailing them to you.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">17.7 Language</h3>
              <p>
                These Terms are written in English. Any translated version is provided solely for convenience. To the extent any translated version conflicts with the English version, the English version controls.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">18. Apple iOS Application</h2>
              <p>
                With respect to the Nordex mobile application made available for iOS devices (the "iOS App"), the following additional terms apply:
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.1 Acknowledgment</h3>
              <p>
                You acknowledge that these Terms are between you and Nordex only, not with Apple Inc. ("Apple"), and that Apple is not responsible for the iOS App or its content.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.2 License Scope</h3>
              <p>
                Your license to use the iOS App is limited to a non-transferable license to use the iOS App on an Apple-branded product that you own or control, as permitted by the Usage Rules in Apple's Media Services Terms and Conditions.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.3 Maintenance and Support</h3>
              <p>
                Apple has no obligation to provide maintenance or support services for the iOS App. Any maintenance and support is Nordex's responsibility.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.4 Warranty</h3>
              <p>
                Apple provides no warranty for the iOS App. In the event of any failure of the iOS App to conform to any applicable warranty, you may notify Apple, and Apple will refund any purchase price paid (if any). To the maximum extent permitted by law, Apple has no other warranty obligation, and any other claims, losses, liabilities, damages, costs, or expenses are Nordex's responsibility.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.5 Product Claims</h3>
              <p>
                Nordex, not Apple, is responsible for addressing any claims relating to the iOS App or your use of it, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Product liability claims</li>
                <li>Any claim that the iOS App fails to conform to legal or regulatory requirements</li>
                <li>Claims under consumer protection or similar legislation</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.6 Intellectual Property Claims</h3>
              <p>
                Nordex, not Apple, is responsible for investigating, defending, settling, and discharging any third-party claim that the iOS App infringes that third party's intellectual property rights.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.7 Third-Party Beneficiary</h3>
              <p>
                Apple and Apple's subsidiaries are third-party beneficiaries of these Terms with respect to the iOS App, and Apple will have the right to enforce these Terms against you as a third-party beneficiary.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.8 U.S. Government Restrictions</h3>
              <p>
                You represent that:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>You are not located in a country subject to a U.S. Government embargo or designated as a "terrorist supporting" country</li>
                <li>You are not listed on any U.S. Government prohibited or restricted parties list</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.9 Third-Party Terms</h3>
              <p>
                You must comply with applicable third-party terms when using the iOS App, including your wireless data service agreement.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">18.10 Contact</h3>
              <p>
                Questions, complaints, or claims regarding the iOS App should be directed to Nordex via our <a href="/contact" className="text-primary hover:underline">contact page</a>, not to Apple.
              </p>
            </section>

            <hr className="border-border my-8" />

            <section>
              <h2 className="text-xl font-semibold mb-4">19. Contact Information</h2>
              <p>
                If you have any questions about these Terms or the Interface, please contact us:
              </p>
              <div className="mt-4 bg-card border border-border rounded-lg p-4">
                <p className="font-semibold">Nordex / AltariGroup</p>
                <p className="mt-2">
                  Contact:{" "}
                  <a 
                    href="/contact" 
                    className="text-primary hover:underline"
                  >
                    Contact Us
                  </a>
                </p>
                <p className="mt-2">
                  Website:{" "}
                  <a 
                    href="https://nordex.trade" 
                    className="text-primary hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://nordex.trade
                  </a>
                </p>
              </div>
            </section>
          </div>

        </div>
    </SupportLayout>
  );
};
